import { Controller } from "@hotwired/stimulus"

/**
 * ET Reference Simple Controller
 * Simple dropdown selector for ET Reference Block (single selection)
 * Auto-populates model and serial number when reference block is selected
 */
export default class extends Controller {
  static targets = ["select", "model", "serialNo", "hidden"]

  connect() {
    console.log("[et-reference-simple] Controller connected")

    // Handle select2 events if jQuery and select2 are available
    if (window.jQuery && this.hasSelectTarget) {
      const $select = window.jQuery(this.selectTarget)

      // Listen to select2:select event for when an option is chosen
      $select.on('select2:select', (e) => {
        this.handleSelection(e.params.data.id)
      })

      // Listen to select2:clear event for when selection is cleared
      $select.on('select2:clear', () => {
        this.clearFields()
        this.updateHiddenField(null)
      })
    }
  }

  disconnect() {
    // Clean up select2 event listeners
    if (window.jQuery && this.hasSelectTarget) {
      window.jQuery(this.selectTarget).off('select2:select select2:clear')
    }
  }

  // Called from Stimulus action (native change event)
  async onChange(event) {
    const equipmentId = event.target.value
    await this.handleSelection(equipmentId)
  }

  // Unified handler for both native change and select2 events
  async handleSelection(equipmentId) {
    if (!equipmentId) {
      // Clear fields if no reference block selected
      this.clearFields()
      this.updateHiddenField(null)
      return
    }

    try {
      const response = await fetch(`/equipment/${equipmentId}.json`)
      if (!response.ok) throw new Error('Failed to fetch equipment data')

      const data = await response.json()

      // Update display fields
      if (this.hasModelTarget) {
        this.modelTarget.value = data.model || ''
      }
      if (this.hasSerialNoTarget) {
        this.serialNoTarget.value = data.serial_no || 'Missing serial number'
      }

      // Update hidden field for form submission
      this.updateHiddenField(equipmentId)

      // Dispatch event for ET auto-populate (method specific variables)
      document.dispatchEvent(new CustomEvent('equipment:added', {
        detail: {
          equipmentId: equipmentId,
          equipmentTypeName: 'ET Reference Block',
          equipmentData: data
        }
      }))

      // Trigger autosave
      this.triggerAutosave()

    } catch (error) {
      console.error("[et-reference-simple] Error:", error)
    }
  }

  clearFields() {
    if (this.hasModelTarget) this.modelTarget.value = ''
    if (this.hasSerialNoTarget) this.serialNoTarget.value = ''
  }

  updateHiddenField(equipmentId) {
    // Get the instrument's currently selected value to preserve it
    const instrumentSelect = document.querySelector('[data-et-instrument-simple-target="select"]')
    const instrumentId = instrumentSelect ? instrumentSelect.value : null

    // The instrument controller's hidden target manages both equipment IDs
    // We need to update it with both values
    const instrumentHiddenContainer = document.querySelector('[data-et-instrument-simple-target="hidden"]')

    if (instrumentHiddenContainer) {
      // Build combined hidden fields for both equipment types
      let hiddenFields = ''

      if (instrumentId) {
        hiddenFields += `<input type="hidden" name="report[equipment_ids][]" value="${instrumentId}" />\n`
      }

      if (equipmentId) {
        hiddenFields += `<input type="hidden" name="report[equipment_ids][]" value="${equipmentId}" />`
      }

      instrumentHiddenContainer.innerHTML = hiddenFields

      // Clear our own hidden container since instrument controller manages both
      if (this.hasHiddenTarget) {
        this.hiddenTarget.innerHTML = ''
      }
    } else if (this.hasHiddenTarget) {
      // Fallback: if instrument container not found, just update our own
      if (equipmentId) {
        this.hiddenTarget.innerHTML = `<input type="hidden" name="report[equipment_ids][]" value="${equipmentId}" />`
      } else {
        this.hiddenTarget.innerHTML = ''
      }
    }
  }

  triggerAutosave() {
    const changeEvent = new Event('change', { bubbles: true })
    this.element.dispatchEvent(changeEvent)
  }
}
